import React, { useContext } from 'react';
import styled from 'styled-components';
import { BsChat, BsTelephone } from 'react-icons/bs';
import { LocaleContext } from '../../contexts/LocaleContext';
import { SiteSettingsContext } from '../../contexts/SiteSettingsContext';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { GetInternalLinkFunctionType } from '../../models/GetInternalLinkFunctionType';
import {
  SanityCopyright,
  SanityFooterColumns,
  SanityFooterColumnsItems,
  SanitySocialMediaLinks,
} from '../../models/gatsby-graphql-types';
import LinkComponent from '../LinkComponent';
import SocialIcons from './SocialIcons';
import sharedStyles from '../styles';
import breakpoints from '../breakpoints';

// For cookie-pro script
declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
  }
}

const FooterContainer = styled.footer`
  padding: ${sharedStyles.spacing['spacing-2xl']} 0;
  background-color: ${({ theme }) => theme.footer.backgroundColor};
  color: ${({ theme }) => theme.footer.fontColor};
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: ${breakpoints.xl};
  padding: 0 ${sharedStyles.spacing['spacing-lg']};

  .underline {
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }

  a {
    color: ${({ theme }) => theme.footer.fontColor};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (min-width: ${breakpoints.md}) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const FooterColumn = styled.div`
  h2 {
    font-size: 1.5rem;
    color: inherit;
  }
  a svg {
    margin-right: ${sharedStyles.spacing['spacing-1xs']};
  }
`;

const FooterList = styled.ul`
  list-style: none;
  margin: 0 0 ${sharedStyles.spacing['spacing-lg']};
  padding: 0;
`;

const LegalLinks = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li > button {
    background: transparent;
    border: none;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    padding: 0;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  @media screen and (min-width: ${breakpoints.md}) {
    li {
      display: inline-block;
      margin-right: 0.6rem;
    }
  }
`;

const FooterListItem = styled.li`
  margin-top: ${sharedStyles.spacing['spacing-1xs']};
  svg {
    vertical-align: middle;
  }
`;

const parseSanityLink = (
  sanityLink: SanityFooterColumnsItems,
  getInternalLink: GetInternalLinkFunctionType
) => {
  if (sanityLink?.linkType === 'internal') {
    return (
      <LinkComponent
        to={getInternalLink({
          ...sanityLink,
          reference: sanityLink.internalLink,
        })}
      >
        {sanityLink.footerLinkName}
      </LinkComponent>
    );
  }

  return (
    <a href={sanityLink?.href} target={sanityLink?.blank ? `_blank` : `_self`}>
      {sanityLink.footerLinkName}
    </a>
  );
};

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void; // Define the method signature
    };
  }
}

export type FooterProps = {
  ChatComponent: React.FC;
  columns: SanityFooterColumns[];
  footerCopyright: SanityCopyright;
  footerLegal: SanityFooterColumnsItems[];
  footerSocialIcons: SanitySocialMediaLinks;
  getInternalLink?: GetInternalLinkFunctionType;
  minimalFooter: boolean;
  hideFromScreenReader?: boolean;
};

function Footer({
  ChatComponent,
  columns,
  footerCopyright,
  footerLegal,
  footerSocialIcons,
  getInternalLink = () => '',
  minimalFooter = false,
  hideFromScreenReader = false,
}: Readonly<FooterProps>) {
  const { tt } = useUiComponentsInternationalContext();

  const localeContext = useContext(LocaleContext);
  const { siteSettings } = useContext(SiteSettingsContext);

  const currency = localeContext?.currency || 'USD';

  const getInternalLinkWithCurrency: GetInternalLinkFunctionType = props =>
    getInternalLink({
      ...props,
      currency,
    });

  return (
    <FooterContainer aria-hidden={hideFromScreenReader}>
      {minimalFooter || (
        <>
          <Container>
            <SocialIcons footerSocialIcons={footerSocialIcons} />
          </Container>
          <Container>
            <FooterRow>
              {columns?.map(column => (
                <FooterColumn key={column.footerColumnHeader}>
                  {!!column.footerColumnHeader?.length && (
                    <h2>{column.footerColumnHeader}</h2>
                  )}
                  <FooterList>
                    {column.footerColumnItems?.map(item => (
                      <FooterListItem key={item.footerLinkName}>
                        {parseSanityLink(item, getInternalLinkWithCurrency)}
                      </FooterListItem>
                    ))}
                  </FooterList>
                </FooterColumn>
              ))}

              <FooterColumn>
                <h2>{tt(s => s.footerCommon.contactUsHeader)}</h2>
                <FooterList>
                  <FooterListItem>
                    <BsChat aria-hidden /> <ChatComponent />
                  </FooterListItem>
                  <FooterListItem>
                    {siteSettings?.phone && (
                      <a href={`tel:${siteSettings.phone.replace(/\D/g, '')}`}>
                        <BsTelephone aria-hidden />
                        {siteSettings.phone}
                      </a>
                    )}
                  </FooterListItem>
                </FooterList>
              </FooterColumn>
            </FooterRow>
          </Container>
        </>
      )}

      <Container>
        &#169; {new Date().getFullYear()}{' '}
        <a className="underline" href={footerCopyright.copyrightUrl}>
          {footerCopyright.copyrightName}
        </a>{' '}
        {tt(s => s.footerCommon.allRightsReserved)}
      </Container>

      <Container>
        <LegalLinks>
          {footerLegal?.map(legalItem => (
            <li key={legalItem.footerLinkName}>
              {parseSanityLink(legalItem, getInternalLinkWithCurrency)}
            </li>
          ))}
          <li key="cookie-settings">
            <button
              type="button"
              aria-label={tt(
                s => s.footerCommon.openCookieSettingsModalAriaLabel
              )}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window?.OneTrust?.ToggleInfoDisplay();
                }
              }}
            >
              {tt(s => s.footerCommon.cookieSettingsButton)}
            </button>
          </li>
        </LegalLinks>
      </Container>
    </FooterContainer>
  );
}

export default Footer;
